import React, { useState } from 'react';
import { Box, Typography, Tabs, Tab, alpha, useTheme, useMediaQuery } from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditNoteIcon from '@mui/icons-material/EditNote';
import HistoryIcon from '@mui/icons-material/History';
import CashMemoPunchForm from '../features/cashmemo/CashMemoPunchForm';
import CashMemoHistoryTable from '../features/cashmemo/CashMemoHistoryTable';
import CashMemoUpdate from 'features/cashmemo/CashUpdate';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`cashmemo-tabpanel-${index}`}
            aria-labelledby={`cashmemo-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `cashmemo-tab-${index}`,
        'aria-controls': `cashmemo-tabpanel-${index}`,
    };
}

const CashMemoPage = () => {
    const [value, setValue] = useState(0);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleChange = (_: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    const tabs = [
        { label: 'Create Memo', icon: <AddCircleOutlineIcon fontSize="small" /> },
        { label: 'Update Memo', icon: <EditNoteIcon fontSize="small" /> },
        { label: 'History', icon: <HistoryIcon fontSize="small" /> },
    ];

    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
            {/* Page Header */}
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.primary.light} 100%)`,
                    px: { xs: 2, sm: 4 },
                    py: { xs: 2.5, sm: 3.5 },
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    borderRadius: 1,
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                }}
            >
                <Box
                    sx={{
                        bgcolor: alpha('#fff', 0.15),
                        borderRadius: 2,
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <ReceiptIcon sx={{ color: '#fff', fontSize: { xs: 28, sm: 36 } }} />
                </Box>
                <Box>
                    <Typography
                        variant={isMobile ? 'h5' : 'h4'}
                        fontWeight={700}
                        sx={{ color: '#fff', lineHeight: 1.2 }}
                    >
                        Cash Memo
                    </Typography>
                    <Typography variant="body2" sx={{ color: alpha('#fff', 0.8), mt: 0.3 }}>
                        Create, update and review cash memo records
                    </Typography>
                </Box>
            </Box>

            {/* Tab Container */}
            <Box sx={{ px: { xs: 1, sm: 3 }, pt: 2 }}>
                <Box
                    sx={{
                        bgcolor: 'background.paper',
                        borderRadius: 3,
                        boxShadow: `0 2px 12px ${alpha(theme.palette.grey[500], 0.12)}`,
                        overflow: 'hidden',
                    }}
                >
                    {/* Tabs */}
                    <Box
                        sx={{
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            px: { xs: 1, sm: 2 },
                            pt: 1,
                            bgcolor: alpha(theme.palette.primary.main, 0.03),
                        }}
                    >
                        <Tabs
                            value={value}
                            onChange={handleChange}
                            aria-label="cash memo tabs"
                            variant={isMobile ? 'fullWidth' : 'standard'}
                            TabIndicatorProps={{
                                sx: {
                                    height: 3,
                                    borderRadius: '3px 3px 0 0',
                                    bgcolor: 'primary.main',
                                },
                            }}
                            sx={{
                                minHeight: 52,
                                '& .MuiTab-root': {
                                    minHeight: 52,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    color: 'text.secondary',
                                    gap: 0.75,
                                    '&.Mui-selected': {
                                        color: 'primary.main',
                                    },
                                },
                            }}
                        >
                            {tabs.map((tab, i) => (
                                <Tab
                                    key={tab.label}
                                    label={tab.label}
                                    icon={tab.icon}
                                    iconPosition="start"
                                    {...a11yProps(i)}
                                />
                            ))}
                        </Tabs>
                    </Box>

                    {/* Tab Panels */}
                    <Box sx={{ px: { xs: 1.5, sm: 3 }, pb: 3 }}>
                        <TabPanel value={value} index={0}>
                            <CashMemoPunchForm />
                        </TabPanel>
                        <TabPanel value={value} index={1}>
                            <CashMemoUpdate isActive={value === 1} />
                        </TabPanel>
                        <TabPanel value={value} index={2}>
                            <CashMemoHistoryTable isActive={value === 2} />
                        </TabPanel>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default CashMemoPage;
